/**
 * Station sites (plan §5 R4; spec §3): how many, which types and factions, where they sit and what they are called.
 * Every draw comes from the caller's rng (`deriveSeed(galaxy.seed, 'layout', index, 'stations')`).
 */
import { logger } from '../../core/logger'
import { nextFloat, range, type Rng } from '../../core/rng'
import { deriveSeed, intRange, pickWeighted } from '../seed'
import type { AnchorId, Archetype, RegionId, StarSystem } from '../types'
import { FACTION_KEYS, type FactionsConfig, type ReachSystemsConfig } from './config'
import { add, distance, normalize, quatLookNegZ, scale, shellGap, unitFromUniform } from './frame'
import type { BodySite, FactionKey, FieldSite, StarSite, StationSite, StationType, Vec3T } from './types'

/** Warning (with `{ systemId, type }`) when a station found no clear spot within `placement.maxTries`. */
export const STATION_UNPLACED = 'layout: a station found no clear spot and was dropped'

const ROMAN = ['', ' II', ' III', ' IV', ' V', ' VI', ' VII', ' VIII', ' IX', ' X']

/**
 * The station types a system gets. A region whose `regionStationScale` is 0 (the Rim) gets none, anchors included;
 * otherwise an anchor's list replaces the roll, and the rolled count is the archetype's range × the region scale,
 * rounded, and at least `minStations`.
 */
export function stationTypesFor(
  system: { region: RegionId; archetype: Archetype; anchor: AnchorId | null },
  cfg: ReachSystemsConfig,
  rng: Rng,
): StationType[] {
  const scaleBy = cfg.regionStationScale[system.region]
  if (scaleBy === 0) return []
  if (system.anchor !== null) return [...cfg.anchors[system.anchor]]
  const arch = cfg.archetypeStations[system.archetype]
  const rolled = intRange(rng, arch.count[0], arch.count[1])
  const count = Math.max(Math.round(rolled * scaleBy), cfg.minStations[system.region])
  const weights = arch.types as Record<StationType, number>
  return Array.from({ length: count }, () => pickWeighted(rng, weights))
}

/** One of the type's allowed factions, weighted by the region (missing factions draw with `factionFloor`). */
export function pickStationFaction(rng: Rng, type: StationType, region: RegionId, cfg: ReachSystemsConfig, factions: FactionsConfig): FactionKey {
  const regional = factions.regionWeights[region]
  const weights = {} as Record<FactionKey, number>
  for (const f of cfg.stationTypes[type].factions) weights[f] = Math.max(regional[f] ?? 0, cfg.factionFloor)
  return pickWeighted(rng, weights)
}

/** `<base>`, then `<base> II`, `<base> III`… for duplicates within `taken` (which it updates). */
export function uniqueName(base: string, taken: Set<string>): string {
  for (let n = 0; ; n++) {
    const name = n < ROMAN.length ? `${base}${ROMAN[n]}` : `${base} ${n + 1}`
    if (!taken.has(name)) {
      taken.add(name)
      return name
    }
  }
}

/** True when `p` is outside every shell and at least `starClearanceFactor` star radii from the star. */
export function isClearOfBodies(p: Vec3T, bodies: readonly BodySite[], star: StarSite, cfg: ReachSystemsConfig): boolean {
  return shellGap(p, bodies) > 0 && Math.hypot(p[0], p[1], p[2]) >= cfg.pois.starClearanceFactor * star.radiusM
}

/** The ore with the largest summed weight over the system's fields (null with no fields). */
export function dominantOre(fields: readonly FieldSite[]): string | null {
  const sums = new Map<string, number>()
  for (const f of fields) for (const [ore, w] of Object.entries(f.oreWeights)) sums.set(ore, (sums.get(ore) ?? 0) + w)
  let best: string | null = null
  let bestW = -Infinity
  for (const [ore, w] of sums) {
    if (w > bestW) {
      best = ore
      bestW = w
    }
  }
  return best
}

interface Candidate {
  position: Vec3T
  bay: Vec3T
  anchor: StationSite['anchor']
}

function candidate(rng: Rng, bodies: readonly BodySite[], fields: readonly FieldSite[], cfg: ReachSystemsConfig): Candidate {
  const p = cfg.placement
  const weights = { body: bodies.length > 0 ? p.anchorWeights.body : 0, field: fields.length > 0 ? p.anchorWeights.field : 0, free: p.anchorWeights.free }
  const kind = weights.body + weights.field + weights.free > 0 ? pickWeighted(rng, weights) : 'free'
  if (kind === 'body') {
    const body = bodies[Math.floor(nextFloat(rng) * bodies.length)]
    const toStar = normalize(scale(body.position, -1))
    // The star-facing hemisphere, jittered: toStar + a uniform unit vector stays within 90° of toStar.
    const dir = normalize(add(toStar, unitFromUniform(nextFloat(rng), nextFloat(rng))), toStar)
    const altitude = Math.max(p.bodyAltitudeMinM, body.radiusM * range(rng, p.bodyAltitudeFactor[0], p.bodyAltitudeFactor[1]))
    return { position: add(body.position, scale(dir, body.radiusM + altitude)), bay: dir, anchor: { kind: 'body', id: body.id } }
  }
  if (kind === 'field') {
    const field = fields[Math.floor(nextFloat(rng) * fields.length)]
    const dir = unitFromUniform(nextFloat(rng), nextFloat(rng))
    const position = add(field.center, scale(dir, field.radiusM + p.fieldEdgeOffsetM))
    return { position, bay: normalize(position), anchor: { kind: 'field', id: field.id } }
  }
  const r = range(rng, p.freeOrbitM[0], p.freeOrbitM[1])
  const a = range(rng, 0, Math.PI * 2)
  const position: Vec3T = [r * Math.cos(a), 0, r * Math.sin(a)]
  return { position, bay: normalize(position), anchor: { kind: 'free', id: null } }
}

export interface PlaceStationsInput {
  galaxySeed: number
  system: StarSystem
  star: StarSite
  bodies: readonly BodySite[]
  fields: readonly FieldSite[]
  /** Stations already in the system (the home cluster); new ones keep their separation from these too. */
  existing: readonly StationSite[]
  cfg: ReachSystemsConfig
  factions: FactionsConfig
}

/**
 * Rolls and places the system's generated stations. Each try re-rolls the anchor; a spot must be `minSeparationM`
 * from every other station, outside every shell and clear of the star. A station with no clear spot after
 * `maxTries` is dropped with a warning. The bay (−Z) points away from the anchor body, or from the star.
 */
export function placeStations(input: PlaceStationsInput, rng: Rng): StationSite[] {
  const { system, star, bodies, fields, existing, cfg, factions } = input
  const types = stationTypesFor(system, cfg, rng)
  const placed: StationSite[] = []
  const taken = new Set(existing.map((s) => s.name))
  const specialty = system.reasons.includes('market-specialty') ? dominantOre(fields) : null
  for (const type of types) {
    const faction = pickStationFaction(rng, type, system.region, cfg, factions)
    let spot: Candidate | null = null
    for (let t = 0; t < cfg.placement.maxTries && spot === null; t++) {
      const c = candidate(rng, bodies, fields, cfg)
      if (!isClearOfBodies(c.position, bodies, star, cfg)) continue
      if (existing.some((s) => distance(s.position, c.position) < cfg.placement.minSeparationM)) continue
      if (placed.some((s) => distance(s.position, c.position) < cfg.placement.minSeparationM)) continue
      spot = c
    }
    if (spot === null) {
      logger.warn(STATION_UNPLACED, { systemId: system.id, type })
      continue
    }
    const t = cfg.stationTypes[type]
    const index = existing.length + placed.length
    placed.push({
      id: `${system.id}-st${index}`,
      index,
      name: uniqueName(`${system.name} ${t.label}`, taken),
      type,
      faction,
      look: factions.factions[faction].look,
      assetId: t.assetId,
      position: spot.position,
      rotation: quatLookNegZ(spot.bay),
      services: [...t.services],
      m2Role: t.m2Role,
      specialty: t.services.includes('market') ? specialty : null,
      priceSeed: deriveSeed(input.galaxySeed, 'layout', system.index, 'price', index),
      anchor: spot.anchor,
    })
  }
  return placed
}

/** The faction with the most stations; ties go to the higher region weight, then factions.json order. Null with none. */
export function ownerOf(stations: readonly Pick<StationSite, 'faction'>[], region: RegionId, factions: FactionsConfig): FactionKey | null {
  if (stations.length === 0) return null
  const weights = factions.regionWeights[region]
  let best: FactionKey | null = null
  let bestCount = 0
  let bestWeight = -1
  for (const f of FACTION_KEYS) {
    const count = stations.filter((s) => s.faction === f).length
    const weight = weights[f] ?? 0
    if (count > bestCount || (count === bestCount && count > 0 && weight > bestWeight)) {
      best = f
      bestCount = count
      bestWeight = weight
    }
  }
  return best
}
